import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getAllEndPricingTypes,
  createPricing,
  getCategoriesFiltered,
  getBranchOfficesFiltered,
  getCurrencies,
  getBrandsFiltered,
  getArticlesFiltered,
  getArticleGroups,
  getArticlesByName,
} from "../../api/Services";
import {
  Form,
  message,
  Select,
  Button,
  Input,
  DatePicker,
  InputNumber,
  Col,
  Row,
  Space,
  Card,
  Statistic,
} from "antd";

import { BusinessUniteContext } from "../../components/layout/Layout";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import {
  DeleteOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import styles from "./NewPricing.module.scss";
import { getOrganizationId } from "../../api/Helper";

const NewPricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { contextValue: businessUnitSelected } =
    useContext(BusinessUniteContext);

  const [loading, setLoading] = useState(false);
  const [branchOffices, setBranchOffices] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [endPricingTypes, setEndPricingTypes] = useState<any[]>([]);
  const [pricingDates, setPricingDates] = useState([{ key: 0 }]);
  const [articles, setArticles] = useState<any[]>([]);
  const [articleGroups, setArticleGroups] = useState<any[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [articleOptions, setArticleOptions] = useState<any[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<any[]>([]);
  const [selectedArticleGroups, setSelectedArticleGroups] = useState<any[]>([]);
  const [simulatorPrice, setSimulatorPrice] = useState<number>(0);
  const [simulatorCost, setSimulatorCost] = useState<number>(0);
  const [isOrg5, setIsOrg5] = useState(false);

  const [simArticle, setSimArticle] = useState<any>({
    defaultPrice: 0,
    cost: 0,
  });

  const [suggestedPrice, setSuggestedPrice] = useState<number>(0);
  const [projectedVolume, setProjectedVolume] = useState<number>(0);

  useEffect(() => {
    const fetchOrg = async () => {
      const orgId = await getOrganizationId();
      const articleId = searchParams.get('articleId');
      
      if (parseInt(orgId) === 5) {
        setIsOrg5(true);
        
        if (articleId === '11') {
          setSimArticle({ defaultPrice: 1200, cost: 40 });
        } else {
          setSimArticle({ defaultPrice: 1200, cost: 8002 });
        }
        
        const min = 1200 * 0.8;
        const max = 1200 * 1.2;
        setSuggestedPrice(Math.round(Math.random() * (max - min) + min));
        setProjectedVolume(Math.floor(Math.random() * 500) + 501);
      } else {
        setIsOrg5(false);
        setSimArticle({ defaultPrice: 0, cost: 0 });
        setSuggestedPrice(0);
        setProjectedVolume(0);
      }
    };
    fetchOrg();
  }, [searchParams]);

  useEffect(() => {
    const updateVolume = async () => {
      const orgId = await getOrganizationId();
      if (parseInt(orgId) === 5) {
        setProjectedVolume(Math.floor(Math.random() * 500) + 501);
      }
    };
    updateVolume();
  }, [simulatorPrice]);

  const currentMargin = simArticle.defaultPrice && simArticle.cost
    ? ((simArticle.defaultPrice - simArticle.cost) / simArticle.defaultPrice * 100).toFixed(2)
    : 0;
  const simulatedMargin = simulatorPrice && simArticle.cost
    ? ((simulatorPrice - simArticle.cost) / simulatorPrice * 100).toFixed(2)
    : 0;
  const unitProfit = simulatorPrice && simArticle.cost
    ? (simulatorPrice - simArticle.cost).toFixed(2)
    : 0;
  const projectedRevenue = simulatorPrice && projectedVolume
    ? (simulatorPrice * projectedVolume).toFixed(2)
    : 0;
  const projectedProfit = simulatorPrice && simArticle.cost && projectedVolume
    ? ((simulatorPrice - simArticle.cost) * projectedVolume).toFixed(2)
    : 0;

  const [newPricingForm] = Form.useForm();
  const [articlesForm] = Form.useForm();
  const [articleGroupsForm] = Form.useForm();

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_PRICINGS)) {
      handleFetchEndPricingTypes();
      handleFetchCurrencies();
      handleSearchBrands("");
      handleSearchCategories("");
      handleSearchArticles("");
    }
  }, []);

  const addPricingDate = () => {
    setPricingDates([...pricingDates, { key: pricingDates.length }]);
  };

  const removePricingDate = (key: any) => {
    setPricingDates(pricingDates.filter((item) => item.key !== key));
    const currentValues = newPricingForm.getFieldValue("pricingDates") || [];
    newPricingForm.setFieldsValue({
      pricingDates: currentValues.filter((_: any, index: any) => index !== key),
    });
  };

  const handleFetchArticles = async (nameFilterValue: string) => {
    const response = await getArticlesFiltered(0, {
      articleName: nameFilterValue,
    });
    setArticles(response.articles);
  };

  const handleFetchArticleGroups = async (nameFilterValue: string) => {
    const response = await getArticleGroups(0, {
      name: nameFilterValue,
    });
    setArticleGroups(response.articleGroups);
  };

  const handleFetchCurrencies = async () => {
    const response = await getCurrencies();
    setCurrencies(response);
  };

  const handleSearchBranchOffices = async (value: string) => {
    if (businessUnitSelected) {
      const response = await getBranchOfficesFiltered(0, {
        name: value,
        businessUnitId: businessUnitSelected,
      });
      setBranchOffices(response.branchOffices);
    } else setBranchOffices([]);
  };

  const handleSearchBrands = async (value: string) => {
    const response = await getBrandsFiltered(value);
    setBrands(response);
  };

  const handleSearchCategories = async (value: string) => {
    if (value.length > 2) {
      const response = await getCategoriesFiltered(value);
      setCategories(response);
    } else setCategories([]);
  };

  const handleSearchArticles = async (value: string) => {
    setLoadingArticles(true);
    const response = await getArticlesByName(value);
    const options = response.articles.map((item: any) => ({
      value: item.id,
      text: item.name,
    }));
    setArticleOptions(options);
    setLoadingArticles(false);
  };

  const handleFetchEndPricingTypes = async () => {
    const response = await getAllEndPricingTypes();
    setEndPricingTypes(response);
  };

  const calculateMargin = (price: number, cost: number) => {
    if (!price || !cost) return 0;
    return ((price - cost) / price * 100);
  };

  const calculateSuggestedPrice = (cost: number) => {
    return cost * 1.6;
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const getOptionsCategories = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      const parentsString = item?.parents
        ? item.parents.filter(Boolean).join(" - ")
        : "";
      const categoryName = item?.name || "";

      const categoryTree = parentsString
        ? `${parentsString} - ${categoryName}`
        : categoryName;

      options.push(
        <Select.Option value={item.id} key={item.id}>
          {categoryTree}
        </Select.Option>
      );
    });

    return options;
  };

  const getOptionsEndTypes = (items: any) => {
    const options: any[] = [];
    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.value} key={item.value}>
          {item.description_es}
        </Select.Option>
      );
    });

    return options;
  };

  const handleCreate = async () => {
    await newPricingForm.validateFields();

    setLoading(true);

    const data = newPricingForm.getFieldsValue();

    const {
      name,
      description,
      currencyId,
      brandId,
      value,
      endPrice,
      newPrice,
      branchOfficeIds,
      categoryIds,
    } = data;

    const articlesIds = articlesForm.getFieldsValue();
    const groupIds = articleGroupsForm.getFieldsValue();

    const dates = data.pricingDates
      .map((range: any) => {
        const startDate = range
          ? range[0]?.startOf("day").format("YYYY-MM-DDTHH:mm:ss")
          : null;
        const endDate = range
          ? range[1]?.endOf("day").format("YYYY-MM-DDTHH:mm:ss")
          : null;

        return { startDate, endDate };
      })
      .filter((date: any) => date.startDate !== null || date.endDate !== null);

    let pricingInfo: any = {
      name,
      description,
      currencyId,
      brandId,
      categoryIds,
      value,
      newPrice,
      endPrice,
      branchOfficeIds,
      articlesIds: articlesIds.articles,
      articleGroups: groupIds.groupArticles,
      dates,
    };

    const response = await createPricing(pricingInfo);

    if (response) {
      message.success(t("Common-CreateSuccessMessage"));
      newPricingForm.resetFields();
      navigate("/pricings");
    } else message.error(t("Common-ErrorMessage"));

    setLoading(false);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-NewPricing")}</h1>
      </div>
      <div className={styles.contentWrapper}>
        
        <div className={styles.articlesWrapper}>
          <h1 className={styles.articlesTitle}>{t("Common-Articles")}</h1>
          <div className={styles.articlesFormWrapper}>
            <Form
              name="articles"
              layout="vertical"
              initialValues={{ remember: true }}
              autoComplete="off"
              className={styles.articleForm}
              form={articlesForm}
            >
              <Form.List name="articles">
                {(fields, { add, remove }, { errors }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space
                        key={key}
                        className={styles.contactSpace}
                        align="baseline"
                      >
                        <Form.Item
                          {...restField}
                          label={t("Common-Article")}
                          className={styles.articleName}
                          name={[name, "articleId"]}
                          rules={[
                            {
                              required: true,
                              message: t("Common-Article"),
                            },
                          ]}
                        >
                          <Select
                            className={styles.articleInfoItem}
                            allowClear
                            placeholder={t("Common-SearchByName")}
                            filterOption={(input: string, option: any) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                            showSearch
                            style={{ width: "100%" }}
                            onSearch={(value) => handleFetchArticles(value)}
                          >
                            {getOptions(articles)}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label={t("Common-OptimalPrice")}
                          className={styles.articleInfoItem}
                          name={[name, "optimalPrice"]}
                          rules={[
                            {
                              required: true,
                              message: t("Common-OptimalPrice"),
                            },
                          ]}
                        >
                          <Input
                            className={styles.articleInfoItem}
                            placeholder={t("Common-OptimalPrice")}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label={t("Common-PriceCompetition")}
                          className={styles.articleInfoItem}
                          name={[name, "priceCompetition"]}
                          rules={[
                            {
                              required: true,
                              message: t("Common-PriceCompetition"),
                            },
                          ]}
                        >
                          <Input
                            className={styles.articleInfoItem}
                            placeholder={t("Common-PriceCompetition")}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label={t("Common-Demand")}
                          className={styles.articleInfoItem}
                          name={[name, "demand"]}
                          rules={[
                            {
                              required: true,
                              message: t("Common-Demand"),
                            },
                          ]}
                        >
                          <Input
                            className={styles.articleInfoItem}
                            placeholder={t("Common-Demand")}
                          />
                        </Form.Item>

                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Form.Item>
                      <Button
                        type="primary"
                        className={styles.actionsButton}
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                      />
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form>
          </div>
        </div>

        <div className={styles.articlesWrapper}>
          <h1 className={styles.articlesTitle}>{t("Common-ArticleGroups")}</h1>
          <div className={styles.articlesFormWrapper}>
            <Form
              name="groupArticles"
              layout="vertical"
              initialValues={{ remember: true }}
              autoComplete="off"
              className={styles.articleForm}
              form={articleGroupsForm}
            >
              <Form.List name="groupArticles">
                {(fields, { add, remove }, { errors }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space
                        key={key}
                        className={styles.contactSpace}
                        align="baseline"
                      >
                        <Form.Item
                          {...restField}
                          label={t("Common-ArticleGroup")}
                          className={styles.articleName}
                          name={[name, "articleGroupId"]}
                          rules={[
                            {
                              required: true,
                              message: t("Common-GroupArticle"),
                            },
                          ]}
                        >
                          <Select
                            className={styles.articleInfoItem}
                            allowClear
                            placeholder={t("Common-SearchByName")}
                            filterOption={(input: string, option: any) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                            showSearch
                            style={{ width: "100%" }}
                            onSearch={(value) =>
                              handleFetchArticleGroups(value)
                            }
                          >
                            {getOptions(articleGroups)}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label={t("Common-OptimalPrice")}
                          className={styles.articleInfoItem}
                          name={[name, "optimalPrice"]}
                          rules={[
                            {
                              required: true,
                              message: t("Common-OptimalPrice"),
                            },
                          ]}
                        >
                          <Input
                            className={styles.articleInfoItem}
                            placeholder={t("Common-OptimalPrice")}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label={t("Common-PriceCompetition")}
                          className={styles.articleInfoItem}
                          name={[name, "priceCompetition"]}
                          rules={[
                            {
                              required: true,
                              message: t("Common-PriceCompetition"),
                            },
                          ]}
                        >
                          <Input
                            className={styles.articleInfoItem}
                            placeholder={t("Common-PriceCompetition")}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label={t("Common-Demand")}
                          className={styles.articleInfoItem}
                          name={[name, "demand"]}
                          rules={[
                            {
                              required: true,
                              message: t("Common-Demand"),
                            },
                          ]}
                        >
                          <Input
                            className={styles.articleInfoItem}
                            placeholder={t("Common-Demand")}
                          />
                        </Form.Item>

                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Form.Item>
                      <Button
                        type="primary"
                        className={styles.actionsButton}
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                      />
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form>
          </div>
        </div>

        <div className={styles.simulatorWrapper}>
          <h1 className={styles.subTitle}>{t("Common-PriceSimulator")}</h1>
          {isOrg5 ? (
            <Card className={styles.simulatorCard}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Precio default"
                    value={simArticle.defaultPrice}
                    prefix="$"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Costo"
                    value={simArticle.cost}
                    prefix="$"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Margen actual"
                    value={currentMargin}
                    suffix="%"
                  />
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={8}>
                  <Statistic
                    title="Precio sugerido"
                    value={suggestedPrice}
                    prefix="$"
                  />
                </Col>
                <Col span={8}>
                  <div className={styles.simulatorInput}>
                    <label>Precio a simular</label>
                    <InputNumber
                      value={simulatorPrice}
                      onChange={(value) => setSimulatorPrice(value || 0)}
                      placeholder="Precio"
                      style={{ width: '100%' }}
                    />
                  </div>
                </Col>
                <Col span={8}>
                  <Statistic
                    title="% Margen"
                    value={simulatedMargin}
                    suffix="%"
                  />
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={8}>
                  <Statistic
                    title="Ganancia estimada por unidad"
                    value={unitProfit}
                    prefix="$"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Volumen proyectado"
                    value={projectedVolume}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Ingresos proyectados"
                    value={projectedRevenue}
                    prefix="$"
                  />
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={8}>
                  <Statistic
                    title="Ganancia proyectada"
                    value={projectedProfit}
                    prefix="$"
                  />
                </Col>
              </Row>
            </Card>
          ) : (
            <Card className={styles.simulatorCard}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <div className={styles.simulatorInput}>
                    <label>{t("Common-Cost")}</label>
                    <InputNumber
                      value={simulatorCost}
                      onChange={(value) => setSimulatorCost(value || 0)}
                      placeholder={t("Common-Cost")}
                      style={{ width: '100%' }}
                    />
                  </div>
                </Col>
                <Col span={8}>
                  <div className={styles.simulatorInput}>
                    <label>{t("Common-Price")}</label>
                    <InputNumber
                      value={simulatorPrice}
                      onChange={(value) => setSimulatorPrice(value || 0)}
                      placeholder={t("Common-Price")}
                      style={{ width: '100%' }}
                    />
                  </div>
                </Col>
                <Col span={8}>
                  <Statistic
                    title={t("Common-Margin")}
                    value={calculateMargin(simulatorPrice, simulatorCost)}
                    precision={2}
                    suffix="%"
                  />
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={8}>
                  <Statistic
                    title={t("Common-SuggestedPrice")}
                    value={calculateSuggestedPrice(simulatorCost)}
                    precision={2}
                    prefix="$"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title={t("Common-SuggestedMargin")}
                    value={calculateMargin(calculateSuggestedPrice(simulatorCost), simulatorCost)}
                    precision={2}
                    suffix="%"
                  />
                </Col>
              </Row>
            </Card>
          )}
        </div>

        <div className={styles.formWrapper}>
          <div className={styles.header}>
            <h1 className={styles.subTitle}>{t("Common-ChangeOfPrice")}</h1>
            <Button
              onClick={handleCreate}
              className={styles.saveButton}
              type="primary"
              loading={loading}
            >
              {t("Common-Add")}
            </Button>
          </div>
          <Form
            name="conditionForm"
            layout="vertical"
            initialValues={{ remember: true }}
            autoComplete="off"
            className={styles.newForm}
            form={newPricingForm}
          >
            <div className={styles.formItemsWrapper}>
              <Form.Item
                label={t("Common-Name")}
                name="name"
                className={styles.doubleItemLine}
                rules={[
                  {
                    required: true,
                    message: t("Common-Type"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={t("Common-Description")}
                name="description"
                className={styles.doubleItemLine}
              >
                <Input />
              </Form.Item>
            </div>
            <div className={styles.formItemsWrapper}>
              <Form.Item
                label={t("Common-Type")}
                name="endPrice"
                className={styles.doubleItemLine}
                rules={[
                  {
                    required: true,
                    message: t("Common-Type"),
                  },
                ]}
              >
                <Select
                  allowClear
                  placeholder={t("Common-Type")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptionsEndTypes(endPricingTypes)}
                </Select>
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.endPrice !== currentValues.endPrice
                }
              >
                {({ getFieldValue }) => {
                  switch (getFieldValue("endPrice")) {
                    case "NEW_PRICE":
                      return (
                        <Form.Item
                          label={t("Common-NewPrice")}
                          name="newPrice"
                          className={styles.doubleItemLine}
                          rules={[
                            {
                              required: true,
                              message: t("Common-NewPrice"),
                            },
                          ]}
                        >
                          <Input placeholder={t("Common-NewPrice")} />
                        </Form.Item>
                      );
                  }
                }}
              </Form.Item>
            </div>
            <div className={styles.formItemsWrapper}>
              <Form.Item
                label={t("Common-Currency")}
                className={styles.doubleItemLine}
                name="currencyId"
              >
                <Select
                  allowClear
                  placeholder={t("Common-Currency")}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(currencies)}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.formItemsWrapper}>
              <Form.Item
                label={t("Common-BranchOffices")}
                name="branchOfficeIds"
                className={styles.doubleItemLine}
              >
                <Select
                  mode="multiple"
                  allowClear
                  className={styles.branchOfficeSelector}
                  onSearch={handleSearchBranchOffices}
                  placeholder={t("Common-SearchByName")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(branchOffices)}
                </Select>
              </Form.Item>
              <Form.Item
                label={t("Common-Brand")}
                name="brandId"
                className={styles.doubleItemLine}
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%" }}
                  placeholder={t("Common-SearchByName")}
                  onSearch={handleSearchBrands}
                >
                  {getOptions(brands)}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.formItemsWrapper}>
              <Form.Item
                label={t("Common-Categories")}
                name="categoryIds"
                className={styles.doubleItemLine}
              >
                <Select
                  mode="multiple"
                  allowClear
                  onSearch={handleSearchCategories}
                  placeholder={t("Common-SearchByName")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptionsCategories(categories)}
                </Select>
              </Form.Item>
            </div>
            {pricingDates.map(({ key }, index) => (
              <Row key={key} gutter={16} align="middle">
                <Col span={10}>
                  <Form.Item
                    name={["pricingDates", index]}
                    label={`${t("Common-Range")} ${index + 1}`}
                  >
                    <DatePicker.RangePicker
                      className={styles.dateSelector}
                      format="YYYY-MM-DD"
                      placeholder={[t("Common-StartDate"), t("Common-EndDate")]}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  {pricingDates.length > 1 && (
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => removePricingDate(key)}
                    />
                  )}
                </Col>
              </Row>
            ))}

            <Form.Item>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addPricingDate}
                className={styles.addButton}
              >
                {t("Common-Add")}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default NewPricing;
